import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import { OrderAttachmentPanel } from "../attachments/OrderAttachmentPanel";
import { trpc } from "../../lib/trpc";

interface WoDetailProps {
  open: boolean;
  woId: string | null;
  onClose: () => void;
}

const STATUS_BADGE: Record<string, { cls: string; label: string }> = {
  draft: { cls: "", label: "Taslak" },
  released: { cls: "b-warn", label: "Serbest" },
  in_progress: { cls: "b-warn", label: "Üretimde" },
  completed: { cls: "b-success", label: "Tamamlandı" },
  cancelled: { cls: "", label: "İptal" },
};

function fmtQty(n: number): string {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n);
}

export function WorkOrderDetailDrawer({ open, woId, onClose }: WoDetailProps) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<null | { title: string; message: React.ReactNode; tone: "danger" | "primary"; confirmLabel: string; run: () => void }>(null);
  const woQ = trpc.workOrder.get.useQuery({ id: woId! }, { enabled: open && !!woId });
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  /** Örnek ürün resmi kapısı — eşik/eksik UÇTAN gelir (mamul dışı WO'da `gerekli: 0` döner). */
  const ornekQ = trpc.productionBatch.sampleGate.useQuery({ woId: woId ?? "" }, { enabled: open && !!woId });
  // Üretim/örnek resmi ekleme = üretim yapan roller (iş emri OLUŞTUR veya TAMAMLA yetkisi) → Saha Operatörü dahil.
  const canEditPhotos = !!meQ.data?.user && (meQ.data.user.isSuperAdmin || (meQ.data.user.permissions ?? []).some((p) => p === "create:work_order" || p === "complete:work_order"));

  const startMut = trpc.workOrder.start.useMutation();
  const completeMut = trpc.workOrder.complete.useMutation();
  const chainMut = trpc.workOrder.produceChain.useMutation();
  const cancelMut = trpc.workOrder.cancel.useMutation();

  const w = woQ.data;
  const sb = w ? STATUS_BADGE[w.status] : null;

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      await qc.invalidateQueries();
    } finally {
      setBusy(false);
    }
  }

  const hasOpenSubWos = (w?.subWos ?? []).some((s) => s.status !== "completed" && s.status !== "cancelled");

  return (
    <Drawer open={open} onClose={onClose} size="lg" title={w ? `İş Emri ${w.code}` : "İş Emri"} subtitle={w ? `${w.producedCode} — ${w.producedName}` : undefined}>
      {woQ.isLoading ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--neutral-500)" }}>Yükleniyor…</div>
      ) : !w ? (
        <div style={{ padding: 32, textAlign: "center", color: "var(--neutral-500)" }}>İş emri bulunamadı.</div>
      ) : (
        <>
          {/* Müşteri (sipariş kaynağı) */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <span style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--neutral-500)" }}>Müşteri</span>
            <b style={{ fontSize: 13.5, color: w.customerName ? "var(--neutral-900)" : "var(--neutral-400)" }}>{w.customerName ?? "Stoğa üretim"}</b>
            {w.sourceOrderCode ? <span className="mono" style={{ fontSize: 11.5, color: "var(--neutral-500)" }}>· Sipariş {w.sourceOrderCode}</span> : null}
          </div>

          {/* Üst özet */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(110px,1fr))", gap: 10, marginBottom: 16 }}>
            <Stat label="Durum" value={<span className={`badge-pill ${sb?.cls ?? ""}`}>{sb?.label}</span>} />
            <Stat label="Planlanan" value={<span className="mono">{fmtQty(w.plannedQty)}</span>} />
            <Stat label="Gerçekleşen" value={<span className="mono">{w.actualQty != null ? fmtQty(w.actualQty) : "—"}</span>} />
            <Stat label="Verim" value={<span className="mono">{w.yieldPct != null ? `%${w.yieldPct.toFixed(0)}` : "—"}</span>} />
            {w.outputLotCode ? <Stat label="Çıktı Lot" value={<span className="mono" style={{ color: "var(--success)" }}>{w.outputLotCode}</span>} /> : null}
          </div>

          {/* Aksiyonlar */}
          {w.status !== "completed" && w.status !== "cancelled" ? (
            <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
              {w.subWos.length > 0 ? (
                <button type="button" className="btn-md primary" disabled={busy || chainMut.isPending}
                  onClick={() => run(() => chainMut.mutateAsync({ id: w.id }))}
                  title="Alt iş emirleri dahil tüm zinciri sırayla üretir">
                  {chainMut.isPending ? "Üretiliyor…" : <><Icon name="zap" size={15} />Tüm Zinciri Üret</>}
                </button>
              ) : null}
              {w.status === "draft" || w.status === "released" ? (
                <button type="button" className="btn-md secondary" disabled={busy || hasOpenSubWos}
                  onClick={() => run(() => startMut.mutateAsync({ id: w.id }))}
                  title={hasOpenSubWos ? "Önce alt iş emirlerini tamamlayın" : "Hammaddeleri FEFO ile tüket"}>
                  Başlat (tüket)
                </button>
              ) : null}
              {w.status === "in_progress" ? (
                <button type="button" className="btn-md primary" disabled={busy}
                  onClick={() => run(() => completeMut.mutateAsync({ id: w.id }))}>
                  Tamamla (çıktı lot)
                </button>
              ) : null}
              <button type="button" className="btn-md danger" disabled={busy || w.status === "in_progress"}
                onClick={() => setConfirm({ title: "İş emrini iptal et?", message: <><b>{w.code}</b> iş emrini (ve alt emirleri) iptal et?</>, tone: "danger", confirmLabel: "İptal Et", run: () => run(() => cancelMut.mutateAsync({ id: w.id })) })}>
                İptal
              </button>
            </div>
          ) : null}

          {(startMut.error || completeMut.error || chainMut.error || cancelMut.error) ? (
            <div className="form-banner-err">{(startMut.error || completeMut.error || chainMut.error || cancelMut.error)?.message}</div>
          ) : null}

          {/* Sub-WO ağacı */}
          {w.subWos.length > 0 ? (
            <>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-700)", marginBottom: 8 }}>
                Alt İş Emirleri ({w.subWos.length}) — önce bunlar üretilmeli
              </div>
              <div className="d-card" style={{ marginBottom: 18 }}>
                <div className="list-card">
                  {w.subWos.map((s) => {
                    const ssb = STATUS_BADGE[s.status] ?? { cls: "", label: s.status };
                    return (
                      <div key={s.id} className="list-item">
                        <span className="mono" style={{ fontWeight: 500, flexShrink: 0 }}>{s.code}</span>
                        <div className="body">
                          <span className="t" style={{ fontWeight: 400, color: "var(--neutral-700)" }}>
                            {s.producedCode} {s.producedName}
                            {s.hasGrandchildren ? <span style={{ color: "var(--info)", fontSize: 10.5, marginLeft: 6, display: "inline-flex", alignItems: "center", gap: 2, verticalAlign: "middle" }}><Icon name="cornerDownRight" size={11} />alt zincir</span> : null}
                          </span>
                        </div>
                        <span className="amount" style={{ color: "var(--neutral-600)" }}>{fmtQty(s.plannedQty)}</span>
                        <span className={`badge-pill ${ssb.cls}`}>{ssb.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          ) : null}

          {/* Consumption lines = reçete kırılımı → `view:recipe` izni olan görür (uç `linesHidden` ile söyler) */}
          <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--neutral-700)", marginBottom: 8 }}>
            Tüketilen Malzemeler{w.linesHidden ? "" : ` (${w.lines.length})`}
          </div>
          {w.linesHidden ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 14px", background: "var(--neutral-50)", border: "1px dashed var(--neutral-300)", borderRadius: "var(--radius-md)", fontSize: 12.5, color: "var(--neutral-500)" }}>
              <Icon name="lock" size={14} />
              Reçete kırılımı mahremdir — malzeme detayı için “Reçete kırılımını görüntüle” (view:recipe) izni gerekir.
            </div>
          ) : (
          <div className="dt2-wrap">
            <table className="dt2">
              <thead>
                <tr>
                  <th>Malzeme</th>
                  <th className="num">Planlanan</th>
                  <th className="num">Tüketilen</th>
                </tr>
              </thead>
              <tbody>
                {w.lines.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <span className="cell-id" style={{ marginRight: 6 }}>{l.itemCode}</span>
                      {l.itemName}
                    </td>
                    <td className="cell-num">
                      {fmtQty(l.plannedQty)}<span className="unit">{l.uom}</span>
                    </td>
                    <td className="cell-num" style={{ color: l.actualQty > 0 ? "var(--danger)" : "var(--neutral-400)" }}>
                      {l.actualQty > 0 ? fmtQty(l.actualQty) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}

          {/* ÖRNEK ÜRÜN RESMİ KAPISI — kullanıcı parti kaydına gelmeden ÖNCE eksiği görsün.
              Eşik uçtan gelir; burada sabit sayı YAZILMAZ. */}
          {ornekQ.data && ornekQ.data.gerekli > 0 ? (
            <div
              className="help"
              role={ornekQ.data.uygun ? undefined : "alert"}
              style={{
                marginTop: 14, padding: "8px 12px", borderRadius: 6,
                border: `1px solid ${ornekQ.data.uygun ? "var(--success)" : "#e6c88a"}`,
                borderLeft: `4px solid ${ornekQ.data.uygun ? "var(--success)" : "#B45309"}`,
                background: ornekQ.data.uygun ? "var(--neutral-50)" : "#fdf8ef",
                color: ornekQ.data.uygun ? "var(--neutral-700)" : "#7c4a08",
              }}
            >
              {ornekQ.data.uygun ? (
                <>Örnek ürün resmi tamam — <b>{ornekQ.data.adet}</b>/{ornekQ.data.gerekli}. Parti kaydı yapılabilir.</>
              ) : (
                <>
                  <b>Parti kaydı için örnek ürün resmi eksik: {ornekQ.data.adet}/{ornekQ.data.gerekli}.</b>{" "}
                  Aşağıdan kategoriyi <b>Örnek Ürün</b> seçip <b>{ornekQ.data.eksik}</b> resim daha yükleyin.
                </>
              )}
            </div>
          ) : null}

          {/* Ürün Resimleri — üretim/örnek ürün fotoğrafları (açıklamalı, izlenebilir, siparişe bağlanır). Belirgin başlık bandı için title verilir. */}
          <OrderAttachmentPanel
            woId={w.id}
            canEdit={canEditPhotos}
            uploadCategory="production"
            categoryOptions={[{ value: "production", label: "Üretim" }, { value: "sample", label: "Örnek Ürün" }]}
            title="Ürün Resimleri"
            subtitle="üretim / örnek ürün fotoğrafları"
          />
        </>
      )}
      <ConfirmDialog
        open={!!confirm}
        title={confirm?.title ?? ""}
        message={confirm?.message}
        tone={confirm?.tone ?? "primary"}
        confirmLabel={confirm?.confirmLabel ?? "Onayla"}
        cancelLabel="Vazgeç"
        onConfirm={() => { const c = confirm; setConfirm(null); c?.run(); }}
        onCancel={() => setConfirm(null)}
      />
    </Drawer>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ background: "var(--neutral-50)", borderRadius: "var(--radius-md)", padding: "8px 10px" }}>
      <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--neutral-500)", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--neutral-900)" }}>{value}</div>
    </div>
  );
}
